// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { LineChart, ChartSeries } from './LineChart';
import { createEditor, Descendant, Text, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, RenderLeafProps, Slate, withReact } from 'slate-react';
import Button from './components/Button';
import { Tabs, Tab } from './components/Tabs';
import katex from 'katex';
import { Dark2 } from './colors';

import {
  ViewElement,
  Variable,
  GraphicalFunction,
  EquationError,
  UnitError,
  variableGf,
} from '@simlin/core/datamodel';

import { defined, Series } from '@simlin/core/common';
import { at } from '@simlin/core/collections';
import { plainDeserialize, plainSerialize } from './drawing/common';
import { CustomElement, FormattedText, CustomEditor } from './drawing/SlateEditor';
import { LookupEditor } from './LookupEditor';
import { errorCodeDescription } from '@simlin/engine';

import styles from './VariableDetails.module.css';

interface VariableDetailsProps {
  variable: Variable;
  viewElement: ViewElement;
  getLatexEquation?: (ident: string) => Promise<string | undefined>;
  onDelete: (ident: string) => void;
  onEquationChange: (
    ident: string,
    newEquation: string | undefined,
    newUnits: string | undefined,
    newDoc: string | undefined,
  ) => void;
  onTableChange: (ident: string, newTable: GraphicalFunction | null) => void;
  activeTab: number;
  onActiveTabChange: (newActiveTab: number) => void;
}

interface VariableDetailsState {
  equationContents: Descendant[];
  equationEditor: CustomEditor;
  unitsContents: Descendant[];
  unitsEditor: CustomEditor;
  notesContents: Descendant[];
  notesEditor: CustomEditor;
  editingEquation: boolean;
  latexEquation: string | undefined;
  latexLoading: boolean;
}

function stringFromDescendants(children: Descendant[]): string {
  return plainSerialize(children);
}

function descendantsFromString(equation: string): CustomElement[] {
  return plainDeserialize('equation', equation);
}

function scalarEquationFor(variable: Variable): string {
  if (variable.type === 'module') return '';
  if (variable.equation.type === 'scalar') {
    return variable.equation.equation;
  } else if (variable.equation.type === 'applyToAll') {
    return '{apply-to-all:}\n' + variable.equation.equation;
  } else {
    return "{ TODO: arrayed variable editing isn't supported yet}";
  }
}

function highlightErrors(
  s: string,
  errors: readonly EquationError[] | undefined,
  unitErrors: readonly UnitError[] | undefined,
  isUnits: boolean,
): CustomElement[] {
  const result = descendantsFromString(s);
  if (!isUnits && errors && errors.length > 0) {
    const err = at(errors, 0);
    if (err.end > 0) {
      const children = defined(result[0]).children as Array<Text>;
      const textChild: string = defined(children[0]).text;

      const beforeText = textChild.substring(0, err.start);
      const errText = textChild.substring(err.start, err.end);
      const afterText = textChild.substring(err.end);

      defined(result[0]).children = [{ text: beforeText }, { text: errText, error: true }, { text: afterText }];
    }
  } else if (unitErrors && unitErrors.length > 0) {
    for (const err of unitErrors) {
      if (isUnits === err.isConsistencyError) {
        continue;
      }
      const children = defined(result[0]).children as Array<Text>;
      const textChild: string = defined(children[0]).text;
      const end = err.end === 0 ? textChild.length : err.end;

      const beforeText = textChild.substring(0, err.start);
      const errText = textChild.substring(err.start, end);
      const afterText = textChild.substring(end);

      const highlighted: FormattedText = isUnits ? { text: errText, error: true } : { text: errText, warning: true };
      defined(result[0]).children = [{ text: beforeText }, highlighted, { text: afterText }];

      break;
    }
  }

  return result;
}

// LaTeX provided by engine (Ast.to_latex); keep trivial passthrough fallback
const passthroughLatex = (s: string) => s;

export class VariableDetails extends React.PureComponent<VariableDetailsProps, VariableDetailsState> {
  private _latexRequestId = 0;
  private _mounted = false;

  constructor(props: VariableDetailsProps) {
    super(props);

    const { variable } = props;

    const equation = highlightErrors(
      scalarEquationFor(variable),
      props.variable.errors,
      props.variable.unitErrors,
      false,
    );
    const units = highlightErrors(props.variable.units, props.variable.errors, props.variable.unitErrors, true);

    this.state = {
      equationEditor: withHistory(withReact(createEditor())) as unknown as CustomEditor,
      equationContents: equation,
      unitsEditor: withHistory(withReact(createEditor())) as unknown as CustomEditor,
      unitsContents: units,
      notesEditor: withHistory(withReact(createEditor())) as unknown as CustomEditor,
      notesContents: descendantsFromString(props.variable.documentation),
      editingEquation: !!(props.variable.errors && props.variable.errors.length > 0),
      latexEquation: undefined,
      latexLoading: false,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.loadLatex();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  componentDidUpdate(prevProps: VariableDetailsProps) {
    if (prevProps.viewElement.ident !== this.props.viewElement.ident) {
      this.loadLatex();
    }
  }

  private async loadLatex() {
    const { getLatexEquation, viewElement } = this.props;
    if (!getLatexEquation) return;

    const ident = viewElement.ident;
    if (!ident) return;

    const requestId = ++this._latexRequestId;
    this.setState({ latexLoading: true, latexEquation: undefined });
    try {
      const latex = await getLatexEquation(ident);
      if (requestId !== this._latexRequestId || !this._mounted) return;
      this.setState({ latexEquation: latex, latexLoading: false });
    } catch {
      if (requestId !== this._latexRequestId || !this._mounted) return;
      this.setState({ latexEquation: undefined, latexLoading: false });
    }
  }

  handleEquationChange = (equation: Descendant[]): void => {
    this.setState({ equationContents: equation });
  };

  handleVariableDelete = (): void => {
    this.props.onDelete(defined(this.props.viewElement.ident));
  };

  handleUnitsChange = (equation: Descendant[]): void => {
    this.setState({ unitsContents: equation });
  };

  handleNotesChange = (equation: Descendant[]): void => {
    this.setState({ notesContents: equation });
  };

  handleEquationCancel = (): void => {
    this.setState({
      equationContents: descendantsFromString(scalarEquationFor(this.props.variable)),
      unitsContents: descendantsFromString(this.props.variable.units),
      notesContents: descendantsFromString(this.props.variable.documentation),
      editingEquation: false,
    });
  };

  handleEquationSave = (): void => {
    const { equationContents, unitsContents, notesContents } = this.state;
    const initialEquation = scalarEquationFor(this.props.variable);
    const initialUnits = this.props.variable.units;
    const initialDocs = this.props.variable.documentation;

    const newEquation = stringFromDescendants(equationContents);
    const newUnits = stringFromDescendants(unitsContents);
    const newDocs = stringFromDescendants(notesContents);
    const equation = initialEquation !== newEquation ? newEquation : undefined;
    const units = initialUnits !== newUnits ? newUnits : undefined;
    const docs = initialDocs !== newDocs ? newDocs : undefined;
    if (equation !== undefined || units !== undefined || docs != undefined) {
      this.props.onEquationChange(defined(this.props.viewElement.ident), equation, units, docs);
    }
  };

  formatValue = (value: number): string => {
    return value.toFixed(3);
  };

  handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    this.props.onActiveTabChange(newValue);
  };

  handleAddLookupTable = (): void => {
    const ident = defined(this.props.viewElement.ident);
    const gf: GraphicalFunction = {
      kind: 'continuous',
      xScale: { min: 0, max: 1 },
      yScale: { min: 0, max: 1 },
      xPoints: undefined,
      yPoints: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
    };
    this.props.onTableChange(ident, gf);
  };

  renderLeaf = (props: RenderLeafProps) => {
    const leaf = props.leaf as FormattedText;
    const isError = !!leaf.error;
    const isWarning = !!leaf.warning;
    const className = isError ? styles.eqnError : isWarning ? styles.eqnWarning : undefined;
    return (
      <span {...props.attributes} className={className}>
        {props.children}
      </span>
    );
  };

  renderEquation() {
    const { equationContents } = this.state;
    const initialEquation = scalarEquationFor(this.props.variable);
    const initialUnits = this.props.variable.units;
    const initialDocs = this.props.variable.documentation;

    const data: Readonly<Array<Series>> | undefined = this.props.variable.data;

    let yMin = 0;
    let yMax = 0;
    const chartSeries: ChartSeries[] = [];
    if (data) {
      const colors = Dark2;
      for (let i = 0; i < data.length; i++) {
        const dataset = data[i];
        const name = data.length === 1 ? 'y' : dataset.name;
        const points: Array<{ x: number; y: number }> = [];
        for (let j = 0; j < dataset.time.length; j++) {
          const y = dataset.values[j];
          points.push({ x: dataset.time[j], y });
          if (y < yMin) yMin = y;
          if (y > yMax) yMax = y;
        }
        chartSeries.push({
          name,
          color: colors[i % colors.length],
          points,
        });
      }
    }

    yMin = Math.floor(yMin);
    yMax = Math.ceil(yMax);

    // enable saving and canceling if the equation has changed
    const equationActionsEnabled =
      initialEquation !== stringFromDescendants(equationContents) ||
      initialUnits !== stringFromDescendants(this.state.unitsContents) ||
      initialDocs !== stringFromDescendants(this.state.notesContents);

    let chartOrErrors;
    const errors = this.props.variable.errors;
    const unitErrors = this.props.variable.unitErrors;
    if (errors || unitErrors) {
      const errorList: Array<React.ReactElement> = [];
      // Each entry below becomes a sibling in the JSX render, so each
      // needs a unique React key.  We allocate keys from a running
      // counter (`errorList.length`) rather than from error codes
      // because the same code can recur within a single variable
      // (e.g. multiple unit mismatches).
      if (errors) {
        errors.forEach((error) => {
          errorList.push(
            <div key={`err-${error.code}-${errorList.length}`} className={styles.errorList}>
              error: {errorCodeDescription(error.code)}
            </div>,
          );
        });
      }
      if (unitErrors) {
        unitErrors.forEach((error) => {
          const details = error.details;
          errorList.push(
            <div key={`unit-${error.code}-${errorList.length}`} className={styles.errorList}>
              unit error: {errorCodeDescription(error.code)}
              {details ? `: ${details}` : undefined}
            </div>,
          );
        });
      }
      chartOrErrors = errorList;
    } else {
      chartOrErrors = (
        <LineChart height={300} series={chartSeries} yDomain={[yMin, yMax]} tooltipFormatter={this.formatValue} />
      );
    }

    const showPreview = !errors && !unitErrors && !this.state.editingEquation;

    const equationStr = stringFromDescendants(equationContents);
    let latexHTML = '';
    if (showPreview) {
      try {
        let latex = this.state.latexEquation ?? passthroughLatex(equationStr);
        // Hint line breaks after common binary operators and commas for nicer wrapping
        const insertBreaks = (s: string): string =>
          s
            .replace(/\\cdot/g, '\\cdot\\allowbreak{} ')
            .replace(/\\times/g, '\\times\\allowbreak{} ')
            .replace(/\+/g, '+\\allowbreak{} ')
            .replace(/-/g, '-\\allowbreak{} ')
            .replace(/=/g, '=\\allowbreak{} ')
            .replace(/,/g, ',\\allowbreak{} ');
        latex = insertBreaks(latex);
        latexHTML = katex.renderToString(latex, { throwOnError: false, displayMode: true });
      } catch {
        // fall back to plain text
        latexHTML = '';
      }
    }

    return (
      <div className={styles.cardContent}>
        {showPreview ? (
          <div
            className={styles.eqnPreview}
            onClick={(e) => this.handlePreviewClick(e, equationStr)}
            dangerouslySetInnerHTML={{ __html: latexHTML }}
          />
        ) : (
          <Slate
            editor={this.state.equationEditor}
            initialValue={this.state.equationContents}
            onChange={this.handleEquationChange}
          >
            <Editable
              className={styles.eqnEditor}
              renderLeaf={this.renderLeaf}
              placeholder="Enter an equation..."
              spellCheck={false}
              autoFocus
              onBlur={() => {
                this.handleEquationSave();
                if (!this.props.variable.errors && !this.props.variable.unitErrors) {
                  this.setState({ editingEquation: false });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  this.setState({ editingEquation: false });
                }
              }}
            />
          </Slate>
        )}

        <Slate
          editor={this.state.unitsEditor}
          initialValue={this.state.unitsContents}
          onChange={this.handleUnitsChange}
        >
          <Editable
            className={styles.unitsEditor}
            renderLeaf={this.renderLeaf}
            placeholder="Enter units..."
            spellCheck={false}
            onBlur={this.handleEquationSave}
          />
        </Slate>

        <Slate
          editor={this.state.notesEditor}
          initialValue={this.state.notesContents}
          onChange={this.handleNotesChange}
        >
          <Editable
            className={styles.notesEditor}
            renderLeaf={this.renderLeaf}
            placeholder="Documentation"
            spellCheck={false}
            onBlur={this.handleEquationSave}
          />
        </Slate>

        <div className={styles.cardActions}>
          <Button size="small" color="secondary" onClick={this.handleVariableDelete} className={styles.buttonLeft}>
            Delete
          </Button>
          <div className={styles.buttonRight}>
            <Button size="small" color="primary" disabled={!equationActionsEnabled} onClick={this.handleEquationCancel}>
              Cancel
            </Button>
            <Button size="small" color="primary" disabled={!equationActionsEnabled} onClick={this.handleEquationSave}>
              Save
            </Button>
          </div>
        </div>

        <hr />
        <br />
        {chartOrErrors}
      </div>
    );
  }

  handlePreviewClick = (e: React.MouseEvent<HTMLDivElement>, equationStr: string) => {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const style = window.getComputedStyle(target);
    const padLeft = parseFloat(style.paddingLeft || '0');
    const padRight = parseFloat(style.paddingRight || '0');
    const usableWidth = Math.max(1, rect.width - padLeft - padRight);
    const clickX = Math.max(0, Math.min(usableWidth, e.clientX - rect.left - padLeft));

    // Map click to text offset.
    // Strategy:
    // 1) Try to resolve the specific KaTeX glyph under the click, map it to the matching ASCII
    //    character, and place the caret immediately after the closest matching occurrence in the
    //    ASCII equation (by proximity to the coarse proportional index).
    // 2) Otherwise, prefer proportional mapping over KaTeX content width.
    // 3) Fall back to a hidden monospace ghost with per-char spans and midpoints.
    // 4) Finally fall back to plain proportional mapping over the container width.
    const computeOffset = (): number => {
      // Utility: map KaTeX glyph to ASCII char
      const mapGlyphToAscii = (ch: string): string => {
        if (ch === '\u00b7' || ch === '\u00d7' || ch === '\u22c5') return '*';
        if (ch === '\u2212') return '-';
        return ch;
      };

      // Build a linear list of KaTeX glyphs with positions to enable context disambiguation
      const buildGlyphs = () => {
        const list: { raw: string; ascii: string; left: number; right: number; top: number; bottom: number }[] = [];
        const root = target.querySelector('.katex') as HTMLElement | null;
        if (!root) return { list, contentRect: target.getBoundingClientRect() };
        const html = target.querySelector('.katex .katex-html, .katex-display .katex-html') as HTMLElement | null;
        const contentRect = (html ?? root).getBoundingClientRect();
        try {
          const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
          while (walker.nextNode()) {
            const tn = walker.currentNode;
            const text: string = tn?.nodeValue ?? '';
            for (let i = 0; i < text.length; i++) {
              const rng = document.createRange();
              rng.setStart(tn, i);
              rng.setEnd(tn, i + 1);
              const r = rng.getBoundingClientRect();
              if (!r || r.width <= 0 || r.height <= 0) continue;
              const raw = text[i];
              const ascii = mapGlyphToAscii(raw);
              list.push({ raw, ascii, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
            }
          }
        } catch {
          // ignore
        }
        try {
          console.log('[CaretMap] glyphs', {
            count: list.length,
            ascii: list.map((g) => g.ascii).join(''),
            first20: list
              .slice(0, 20)
              .map((g) => g.ascii)
              .join(''),
          });
        } catch {}
        return { list, contentRect };
      };

      // Utility: try to find the KaTeX glyph index at click coordinates
      const glyphIndexAtPoint = (glyphs: ReturnType<typeof buildGlyphs>['list']): number => {
        // First, exact hit
        for (let i = 0; i < glyphs.length; i++) {
          const g = glyphs[i];
          if (e.clientX >= g.left && e.clientX <= g.right && e.clientY >= g.top && e.clientY <= g.bottom) {
            try {
              console.log('[CaretMap] exact glyph hit', { index: i, ascii: g.ascii, raw: g.raw });
            } catch {}
            return i;
          }
        }
        // Otherwise nearest on same line (by center distance)
        let best = -1;
        let bestDist = Number.POSITIVE_INFINITY;
        for (let i = 0; i < glyphs.length; i++) {
          const g = glyphs[i];
          const cx = (g.left + g.right) / 2;
          const cy = (g.top + g.bottom) / 2;
          const dx = Math.abs(e.clientX - cx);
          const dy = Math.abs(e.clientY - cy);
          const d = dx + dy * 0.5;
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        }
        try {
          if (best >= 0) {
            const g = glyphs[best];
            console.log('[CaretMap] nearest glyph', { index: best, ascii: g.ascii, raw: g.raw, dist: bestDist });
          } else {
            console.log('[CaretMap] no glyph found');
          }
        } catch {}
        return best;
      };

      // Try glyph-based mapping first
      try {
        const { list: glyphs, contentRect } = buildGlyphs();
        if (glyphs.length > 0) {
          const gidx = glyphIndexAtPoint(glyphs);
          if (gidx >= 0) {
            const center = glyphs[gidx].ascii;
            const prev = gidx > 0 ? glyphs[gidx - 1].ascii : '';
            const next = gidx + 1 < glyphs.length ? glyphs[gidx + 1].ascii : '';
            const pattern = (prev + center + next).trim();
            const len = equationStr.length;
            // Try word-token based mapping for alphanumeric/underscore sequences
            const isTokenChar = (ch: string) => /[A-Za-z0-9_]/.test(ch);
            if (isTokenChar(center)) {
              let li = gidx;
              let ri = gidx;
              while (li - 1 >= 0 && isTokenChar(glyphs[li - 1].ascii)) li--;
              while (ri + 1 < glyphs.length && isTokenChar(glyphs[ri + 1].ascii)) ri++;
              const token = glyphs
                .slice(li, ri + 1)
                .map((g) => g.ascii)
                .join('');
              const posInToken = gidx - li;
              if (token.length > 1) {
                // Prefer nearest occurrence of the token to the coarse proportional index
                const cx = Math.max(0, Math.min(contentRect.width, e.clientX - contentRect.left));
                const coarse = Math.max(0, Math.min(len, Math.round((cx / Math.max(1, contentRect.width)) * len)));
                const candidates: number[] = [];
                let s = 0;
                for (;;) {
                  const idx = equationStr.indexOf(token, s);
                  if (idx === -1) break;
                  candidates.push(idx);
                  s = idx + 1;
                }
                if (candidates.length > 0) {
                  let choiceIdx = candidates[0];
                  let bestDist = Math.abs(choiceIdx - coarse);
                  for (const c of candidates) {
                    const d = Math.abs(c - coarse);
                    if (d < bestDist) {
                      bestDist = d;
                      choiceIdx = c;
                    }
                  }
                  try {
                    console.log('[CaretMap] token match', { token, candidates, choiceIdx, posInToken });
                  } catch {}
                  return choiceIdx + posInToken + 1;
                } else {
                  try {
                    console.log('[CaretMap] token not found', { token });
                  } catch {}
                }
              }
            }
            // Signature neighbor matching for punctuation/operators/digits (and single-char tokens)
            const isSigChar = (ch: string) => /[A-Za-z0-9_()*+\-]/.test(ch);
            const prevSig = (() => {
              for (let k = gidx - 1; k >= 0; k--) if (isSigChar(glyphs[k].ascii)) return glyphs[k].ascii;
              return '';
            })();
            const nextSig = (() => {
              for (let k = gidx + 1; k < glyphs.length; k++) if (isSigChar(glyphs[k].ascii)) return glyphs[k].ascii;
              return '';
            })();
            if (center && (prevSig || nextSig)) {
              const positions: number[] = [];
              for (let i = 0; i < len; i++) if (equationStr[i] === center) positions.push(i);
              const matchScore = (idx: number) => {
                let score = 0;
                if (prevSig) {
                  let j = idx - 1;
                  while (j >= 0 && /\s/.test(equationStr[j])) j--;
                  if (j >= 0 && equationStr[j] === prevSig) score += 2;
                }
                if (nextSig) {
                  let j = idx + 1;
                  while (j < len && /\s/.test(equationStr[j])) j++;
                  if (j < len && equationStr[j] === nextSig) score += 2;
                }
                return score;
              };
              let bestIdx = -1;
              let bestScore = -1;
              for (const p of positions) {
                const s = matchScore(p);
                if (s > bestScore) {
                  bestScore = s;
                  bestIdx = p;
                }
              }
              if (bestIdx >= 0) {
                try {
                  console.log('[CaretMap] sig-neighbor match', { center, prevSig, nextSig, bestIdx, bestScore });
                } catch {}
                return bestIdx + 1;
              }
              try {
                console.log('[CaretMap] sig-neighbor no match', { center, prevSig, nextSig, positions });
              } catch {}
            }

            // Try contextual match: prev+center+next
            if (pattern.length >= 2) {
              const idx = equationStr.indexOf(pattern);
              if (idx >= 0) {
                try {
                  console.log('[CaretMap] context match', { pattern, idx, prev, center, next });
                } catch {}
                const centerOffset = prev ? 1 : 0;
                return idx + centerOffset + 1;
              }
              try {
                console.log('[CaretMap] context not found', { pattern });
              } catch {}
            }
            // Fallback: match just center using nth occurrence by glyph index
            if (center) {
              // Count occurrences among glyphs up to this glyph to get the per-char occurrence index
              let glyphOcc = 0;
              for (let k = 0; k < gidx; k++) if (glyphs[k].ascii === center) glyphOcc++;
              let count = -1;
              for (let i = 0; i < len; i++) {
                if (equationStr[i] === center) {
                  count++;
                  if (count === glyphOcc) {
                    try {
                      console.log('[CaretMap] nth occurrence fallback', { center, glyphOcc, index: i });
                    } catch {}
                    return i + 1;
                  }
                }
              }
              try {
                console.log('[CaretMap] nth occurrence not found', { center, glyphOcc });
              } catch {}
            }
            // Fallback: choose occurrence closest to coarse proportional position
            const cx = Math.max(0, Math.min(contentRect.width, e.clientX - contentRect.left));
            const coarse = Math.max(0, Math.min(len, Math.round((cx / Math.max(1, contentRect.width)) * len)));
            const positions: number[] = [];
            for (let i = 0; i < len; i++) if (equationStr[i] === center) positions.push(i);
            if (positions.length > 0) {
              let choice = positions[0];
              let bestDist = Math.abs(choice - coarse);
              for (const p of positions) {
                const d = Math.abs(p - coarse);
                if (d < bestDist) {
                  bestDist = d;
                  choice = p;
                }
              }
              try {
                console.log('[CaretMap] coarse fallback', { center, coarse, positions, choice });
              } catch {}
              return choice + 1;
            }
            try {
              console.log('[CaretMap] no positions for center', { center });
            } catch {}
          }
        }
      } catch {
        // ignore and try next strategy
      }

      try {
        // Prefer mapping relative to actual KaTeX content box, ignoring padding.
        const katexHtml = target.querySelector('.katex .katex-html, .katex-display .katex-html') as HTMLElement | null;
        if (katexHtml) {
          const contentRect = katexHtml.getBoundingClientRect();
          const cx = Math.max(0, Math.min(contentRect.width, e.clientX - contentRect.left));
          const len = equationStr.length;
          try {
            console.log('[CaretMap] proportional over KaTeX content', { width: contentRect.width, cx, len });
          } catch {}
          return Math.max(0, Math.min(len, Math.round((cx / Math.max(1, contentRect.width)) * len)));
        }
      } catch {
        // ignore and try ghost mapping
      }

      // Precise caret mapping via hidden monospace ghost with per-char spans
      try {
        const ghost = document.createElement('div');
        ghost.style.position = 'absolute';
        ghost.style.left = `${padLeft}px`;
        ghost.style.top = '0';
        ghost.style.visibility = 'hidden';
        ghost.style.whiteSpace = 'pre-wrap';
        ghost.style.pointerEvents = 'none';
        ghost.style.width = `${usableWidth}px`;
        // Match editor font
        ghost.style.fontFamily = "'Roboto Mono', monospace";
        // Try to inherit approximate size
        ghost.style.fontSize = window.getComputedStyle(document.body).fontSize || '16px';
        target.appendChild(ghost);

        const spans: HTMLSpanElement[] = [];
        for (let i = 0; i < equationStr.length; i++) {
          const s = document.createElement('span');
          s.textContent = equationStr[i];
          // Ensure each char is measurable
          s.style.display = 'inline-block';
          ghost.appendChild(s);
          spans.push(s);
        }

        // Build cumulative right edge positions
        const ghostRect = ghost.getBoundingClientRect();
        const rights: number[] = [];
        for (let i = 0; i < spans.length; i++) {
          const r = spans[i].getBoundingClientRect();
          rights.push(r.right - ghostRect.left);
        }

        // Prefer nearest character boundary using midpoints between glyph boxes
        // left edge for i is rights[i-1] (or 0 for i=0)
        let idx = spans.length;
        for (let i = 0; i < spans.length; i++) {
          const left = i === 0 ? 0 : rights[i - 1];
          const right = rights[i];
          const mid = (left + right) / 2;
          if (clickX < mid) {
            idx = i;
            break;
          }
          // If past the midpoint, caret should go after this character; keep scanning
        }

        // Cleanup
        target.removeChild(ghost);
        try {
          console.log('[CaretMap] ghost midpoint idx', { idx });
        } catch {}
        return idx;
      } catch {
        // Fallback to proportional mapping
        const len = equationStr.length;
        try {
          console.log('[CaretMap] container proportional fallback', { usableWidth, clickX, len });
        } catch {}
        return Math.max(0, Math.min(len, Math.round((clickX / usableWidth) * len)));
      }
    };

    const offset = computeOffset();
    try {
      console.log('[CaretMap] final offset', { offset });
    } catch {}

    this.setState({ editingEquation: true }, () => {
      // Focus and set caret after the editor renders
      requestAnimationFrame(() => {
        try {
          const editor = this.state.equationEditor;
          ReactEditor.focus(editor);
          Transforms.select(editor, {
            anchor: { path: [0, 0], offset },
            focus: { path: [0, 0], offset },
          });
        } catch {
          // ignore if selection fails; user can click to place caret
        }
      });
    });
  };

  handleLookupChange = (ident: string, newTable: GraphicalFunction | null) => {
    this.props.onTableChange(ident, newTable);
  };

  renderLookup() {
    const { variable } = this.props;

    let table;
    if (variableGf(variable)) {
      table = <LookupEditor variable={variable} onLookupChange={this.handleLookupChange} />;
    } else {
      table = (
        <div className={styles.cardContent}>
          <Button
            variant="contained"
            color="secondary"
            onClick={this.handleAddLookupTable}
            className={styles.addLookupButton}
          >
            Add lookup table
          </Button>
          <br />
          <i>
            A lookup table is a non-linear function indexed by the variable{"'"}s equation. You edit the function by
            dragging your mouse or finger across the graph.
          </i>
        </div>
      );
    }

    return table;
  }

  render() {
    const { activeTab, viewElement } = this.props;

    const equationType = viewElement.type === 'stock' ? 'Initial Value' : 'Equation';
    const content = activeTab === 0 ? this.renderEquation() : this.renderLookup();
    const lookupTab = viewElement.type === 'stock' ? undefined : <Tab label="Lookup Function" />;

    return (
      <div className={styles.card}>
        <Tabs
          className={styles.inner}
          variant="fullWidth"
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={this.handleTabChange}
          aria-label="Equation details selector"
        >
          <Tab label={equationType} />
          {lookupTab}
        </Tabs>

        {content}
      </div>
    );
  }
}
